import {observable, action, makeObservable, toJS, computed} from 'mobx';
import {filter, sortBy, transform, map, compact, some, values, Dictionary, forEach, groupBy, first, every, uniq, concat,
  keyBy, range, max, countBy, intersection} from 'lodash';

import Port from './Port';
import Zone from './Zone';
import RackStore from './RackStore';
import LinksGroup from './LinksGroup';

import {ctrlNodeWidth, ctrlNodeHeight, ctrlIfcPositions, ctrlEntityTypes, ctrlGridStep,
  ctrlHalfNodeHeight} from '../../cablingMapEditor/const';
import {REDUNDANCY, NODE_ROLES, PEER_LINKS_TYPES, ZONES_ROLE_ORDER, ROLES_NODE_TYPES, NODE_TYPES_ROLES,
  PORT_ROLES,
  mlagKeepaliveVlanId} from '../const';
import {generateLocalId, getTopologicOrder} from '../../cablingMapEditor/utils';
import {tryPlacing, stringFromSpeed, speedFromString, makePortChannelError} from '../utils';
import {NodeRolesType, PeerLinksTypesType, PositionType, InterfacePositionType, WritableKeys} from '../types';
import {ISerializable} from '../../cablingMapEditor/types';

const emptyNode = {
  id: null,
  label: '',
  role: NODE_ROLES.GENERIC,
  pairedWith: null,

  relativePosition: {x: 0, y: 0},
  _order: 0
};

export type NodePropertiesType = WritableKeys<Node>;
export type NodePropertiesObjectType = Partial<Record<NodePropertiesType, any>>;

class Node implements ISerializable {
  @observable id: string = generateLocalId();
  @observable role: NodeRolesType = NODE_ROLES.GENERIC;
  @observable _label: string = '';
  @observable name = generateLocalId();
  @observable tags: string[] = [];
  @observable _logicalDevice: any = null;
  @observable _redundancyProtocol = null;
  @observable.ref pairedWith: Node | null = null;
  @observable ldPorts: Port[] = [];

  @observable relativePosition: PositionType = emptyNode.relativePosition;
  @observable _order;
  @observable isFirstInPair = false;
  @observable portChannelId = 0;
  @observable portChannelIdExt = 0;
  @observable mlagVlanId = mlagKeepaliveVlanId;

  @observable spineLinksCount = 0;
  @observable spineLinksSpeed: string | null = null;

  @observable.ref zone: Zone | null = null;

  @observable stage;

  @observable.ref rackStore: RackStore;

  @action
  setProperty(property: NodePropertiesType, value: unknown, applyToPair?: boolean) {
    Object.assign(this, {[property]: value});
    if (this.isPaired && applyToPair) {
      this.pairedWith!.setProperty(property, value, false);
    }
  }

  @action
  setLogicalDevice(value) {
    this.setProperty('_logicalDevice', value, true);
    this.ldPorts = map(value?.ports, (port) => port.clone(true));
    this.checkLinksToSpines();
  }

  set logicalDevice(value) {
    this.setLogicalDevice(value);
  }

  @computed
  get logicalDevice() {
    return this._logicalDevice;
  }

  set label(value: string) {
    this.setProperty('_label', value, true);
  }

  @computed
  get label(): string {
    return `${this._label}${this.isPaired ? (this.isFirstInPair ? '_1' : '_2') : ''}`;
  }

  set redundancyProtocol(value) {
    if (this.isPaired) {
      this.setProperty('_redundancyProtocol', value, true);
    }
  }

  @computed
  get redundancyProtocol() {
    return this.pairedWith ? (
      this._redundancyProtocol || REDUNDANCY.NO_LAG
    ) : null;
  }

  get isPositioned(): boolean {
    return this.relativePosition.x !== null && this.relativePosition.y !== null;
  }

  get type() {
    return ctrlEntityTypes.NODE;
  }

  @computed
  get roleIndex(): number {
    return -ZONES_ROLE_ORDER.indexOf(this.role);
  }

  @computed
  get isOutsideItsZone(): boolean {
    const y = this.relativePosition?.y ?? 0;
    return (
      y < -ctrlHalfNodeHeight || y + ctrlHalfNodeHeight > (this.zone?.size ?? 0)
    );
  }

  @computed
  get isValid(): boolean {
    return !this.validationErrors?.length;
  }

  @computed
  get isPaired(): boolean {
    return !!this.pairedWith;
  }

  @computed
  get pairOrder(): 0 | 1 {
    return (this.isPaired && !this.isFirstInPair) ? 1 : 0;
  }

  @computed
  get isLeafPair(): boolean {
    return this.isLeaf && this.isPaired;
  }

  @computed
  get isMlagLeafPair(): boolean {
    return this.isLeafPair && this.redundancyProtocol === REDUNDANCY.MLAG;
  }

  @computed
  get isEsiLeafPair(): boolean {
    return this.isLeafPair && this.redundancyProtocol === REDUNDANCY.ESI;
  }

  @computed
  get isEsiAccessSwitchPair(): boolean {
    return this.isAccessSwitch && this.isPaired && this.redundancyProtocol === REDUNDANCY.ESI;
  }

  @computed
  get firstInPair(): Node | null {
    return (this.isPaired && !this.isFirstInPair) ? this.pairedWith : this;
  }

  @computed
  get secondInPair(): Node | null {
    return this.firstInPair?.pairedWith ?? null;
  }

  @computed
  get position(): PositionType {
    return {
      x: this.relativePosition.x,
      y: (this.relativePosition?.y ?? 0) + (this.zone?.y ?? 0)
    };
  }

  @computed
  get isGeneric() {
    return this.role === NODE_ROLES.GENERIC;
  }

  @computed
  get isAccessSwitch() {
    return this.role === NODE_ROLES.ACCESS_SWITCH;
  }

  @computed
  get isLeaf() {
    return this.role === NODE_ROLES.LEAF;
  }

  @computed
  get isSwitch() {
    return this.isLeaf || this.isAccessSwitch;
  }

  @computed
  get idsAffected(): string[] {
    return compact(this.isPaired ? [this.id, this.pairedWith?.id] : [this.id]);
  }

  @computed
  get nodesInvolved(): Node[] {
    return this.isPaired ?
      compact(this.isFirstInPair ? [this, this.pairedWith] : [this.pairedWith, this]) :
      [this];
  }

  @computed
  get topologicOrder() {
    return +getTopologicOrder(this.position) + (this.isFirstInPair ? 0 : ctrlNodeWidth + ctrlGridStep);
  }

  @computed.struct
  get myLinksByType(): Record<PeerLinksTypesType, number> {
    return this.rackStore?.nodeLinksGroupsByPeering[this.id] ?? {};
  }

  @computed.struct
  get myLinksGroups(): LinksGroup[] {
    return this.rackStore.linksGroupsByName[this.name] ?? [];
  }

  @computed.struct
  get myLagLinksGroups(): LinksGroup[] {
    return filter(this.myLinksGroups, 'hasAggregation');
  }

  @computed.struct
  get myPeerLinksGroups(): Dictionary<LinksGroup> {
    return keyBy(
      filter(this.myLinksGroups, 'isPeer'),
      'peeringType'
    );
  }

  @computed
  get portChannelIdsNeeded(): number {
    return this.isGeneric ?
      // For generic nodes port channel ids are needed for LAG links
      max(values(countBy(this.myLagLinksGroups, 'toName'))) || 0 :
      0;
  }

  // Lists port channel ids that cannot be used by this node (taken already)
  @computed
  get takenPortChannelIds(): number[] {
    return uniq(
      concat<number>(
        [],
        ...map(
          this.myLinksGroups,
          ({toNode}) => this.rackStore.switchPortChannelIds[toNode!.name] ?? []
        )
      )
    );
  }

  // Represents node's own port channel ids
  @computed
  get myPortChannelIds(): number[] {
    return this.isGeneric ?
      // For generic port channel ids range can only be solid, without gaps
      (this.portChannelId > 0 && this.portChannelIdExt > 0 ?
        range(this.portChannelId, this.portChannelIdExt + 1) : []
      ) :
      // For the switch it is the port channel ids of its peer links
      compact([this.isLeaf ? this.portChannelId : 0, this.portChannelIdExt]);
  }

  @computed
  get myPortChannelIdsDuplicates(): number[] {
    return this.rackStore?.portChannelsDuplicates?.[this.name] ?? [];
  }

  _getPortChannelDuplicates(pcIds: number[]): number[] {
    const withoutEmpties = compact(pcIds);
    return intersection(this.myPortChannelIdsDuplicates, withoutEmpties);
  }

  @computed
  get hasEnoughPortChannelIds() {
    return this.portChannelIdsNeeded > 0 && this.myPortChannelIds.length < this.portChannelIdsNeeded;
  }

  _getInsufficientPortChannelIdsError() {
    return (this.myPortChannelIds.length > 0 && this.hasEnoughPortChannelIds) ?
      {
        range: (
          <>
            {'Port Channel ID range '}
            <b>{`${this.portChannelId}-${this.portChannelIdExt}`}</b>
            {' is insufficient for '}
            <b>{this.portChannelIdsNeeded}</b>
            {' LAG links'}
          </>
        )
      } :
      {};
  }

  @computed
  get portChannelIdsErrors() {
    if (this.isPaired && !this.isFirstInPair) {
      // Avoid duplicate errors on switch pairs
      return this.pairedWith?.portChannelIdsErrors;
    }

    if (this.isGeneric) {
      // Generic
      return {
        // Check for duplicates
        ...makePortChannelError(this._getPortChannelDuplicates(this.myPortChannelIds)),
        // Check range sufficiency
        ...this._getInsufficientPortChannelIdsError()
      };
    } else if (this.isEsiAccessSwitchPair) {
      // ESI access switch pairs have port channel ids defined for L3 peer links
      return makePortChannelError(this._getPortChannelDuplicates([this.portChannelId]), PEER_LINKS_TYPES.L3_PEER);
    } else if (this.isMlagLeafPair) {
      // MLAG leaf pairs have port channel ids defined for L2 and L3 peer links
      return {
        ...makePortChannelError(this._getPortChannelDuplicates([this.portChannelId]), PEER_LINKS_TYPES.L2_PEER),
        ...makePortChannelError(this._getPortChannelDuplicates([this.portChannelIdExt]), PEER_LINKS_TYPES.L3_PEER)
      };
    }
    return {};
  }

  @computed
  get hasPortChannelErrors() {
    return this.myPortChannelIds.length > 0 && some(this.portChannelIdsErrors);
  }

  @computed.struct
  get usedPorts(): Port[] {
    return this.rackStore.nodesUsedPorts[this.id] || [];
  }

  @computed
  get availablePorts(): Port[] {
    const placement = tryPlacing(this.usedPorts, this.ldPorts);
    if (placement) {
      return filter(placement, {isTaken: false});
    } else {
      this.rackStore?.setError(
        'Unable to place links',
        <p><b>{this.label}</b>{' does not have enough free ports'}</p>
      );
      return [];
    }
  }

  @computed
  get rails(): LinksGroup[] {
    return filter(this.myLinksGroups, (linksGroup) => linksGroup.isInRail && linksGroup.contains([this]));
  }

  @computed
  get genericRailsString(): string {
    return sortBy(map(this.rails, 'railIndex')).join(', ');
  }

  // Validates leaf rails in accordance with the following rules:
  // - Single generic node can only have rails of the same speed
  // - All rails that belong to the same generic node must have the same structure
  // - Rail can consist of only links of the same speed
  @computed
  get railsErrors(): string[] {
    if (!this.isLeaf) return [];

    const railsGrouped = groupBy(this.rails, 'railIndex');
    const genericsLeafRails = {};
    const railsGenerics: Dictionary<Node[]> = transform(
      this.rails,
      (acc, {fromNode, railIndex}) => {
        (acc[railIndex!] ??= []).push(fromNode);
        (genericsLeafRails[fromNode!.id] ??= []).push(railIndex);
      },
      {}
    );

    const duplicates = {}; // To avoid reverse error duplication (1, 0) and (0, 1)
    const genericRailsSpeeds: Dictionary<string[]> = {};
    const errors: string[] = [];
    forEach(
      railsGrouped,
      (linksGroups, railIndex) => {
        const speedString = first(linksGroups)!.speedString;
        // All rail links must have the same speed
        if (!every(linksGroups, {speedString})) {
          errors.push(`Speed mismatch in rail ${railIndex}`);
        }

        // All rail generics only have rail links of this speeed
        const railGenerics = railsGenerics[railIndex];
        const firstGeneric = first(railGenerics);
        const firstGenericRails = firstGeneric?.genericRailsString;
        forEach(
          railGenerics,
          (generic, index) => {
            // Generic node can only have rail links of the same speed
            if (!genericRailsSpeeds[generic.label]) {
              genericRailsSpeeds[generic.label] = sortBy(uniq(map(generic.rails, 'speedString')));
            }

            // All rail generics must have the same set of rails
            if (index && generic.genericRailsString !== firstGenericRails &&
              !duplicates[firstGeneric!.id]?.[generic.id] && !duplicates[generic.id]?.[firstGeneric!.id]) {
              duplicates[firstGeneric!.id] = {...duplicates[firstGeneric!.id], ...{[generic.id]: true}};
              errors.push(`Generics "${firstGeneric!.label}" and "${generic.label}" have different sets of rails: ` +
                `[${firstGenericRails}] vs. [${generic.genericRailsString}]`
              );
            }
          }
        );
      }
    );

    forEach(genericRailsSpeeds, (speeds, genericLabel) => {
      if (speeds.length > 1) {
        errors.push(`Generic "${genericLabel}" hosts rails of different speeds: [${speeds.join(', ')}]`);
      }
    });
    return errors;
  }

  // Makes sure that connection to provided linksGroup is possible (node has enough
  // available ports to host it)
  canConnectTo({speed, fromRole, toRole, count}, forcedCount) {
    // Identify which end of the link is applicable to this node
    const role: NodeRolesType = this.role === fromRole ? toRole : fromRole;
    // Adding links groups port to the list of taken
    return this.canConnectPorts(Port.generate(forcedCount || count, speed, role));
  }

  // Checks if provided amount of ports can fit into the capacity of this node
  canConnectPorts(ports: Port[]) {
    // Get list of taken ports for this node
    const portsNeeded = [...this.usedPorts];
    // Adding links groups port to the list of taken
    portsNeeded.push(...ports);

    return tryPlacing(portsNeeded, this.ldPorts) !== false;
  }

  constructor(props = {}, rackStore: RackStore) {
    if (!rackStore) throw new Error('No store provided upon node initialization!');
    this.rackStore = rackStore;
    makeObservable(this);
    this.init(props);
  }

  @action
  init(props: Dictionary<any>) {
    this.fillWith({...emptyNode, ...{id: generateLocalId()}, ...props});
  }

  @action
  fillWith(data: Dictionary<any>) {
    Object.assign(this, data);
  }

  @action
  resetPosition() {
    this.relativePosition = {...emptyNode.relativePosition};
  }

  @action
  pairWith(node: Node) {
    this.pairedWith = node;
    node.pairedWith = this;
  }

  @action
  moveTo({x, y}: PositionType) {
    this.relativePosition = {x, y};
  }

  @action
  moveBy({x, y}: PositionType, notPropagate?: boolean) {
    this.relativePosition = this.isPositioned ?
      {x: this.relativePosition.x! + x!, y: this.relativePosition.y! + y!} :
      {x, y};
    if (!notPropagate) this.pairedWith?.moveBy({x, y}, true);
  }

  @action
  snapToGrid(): boolean {
    if (!this.isPositioned) return false;

    const x = ctrlGridStep * Math.round(this.relativePosition.x! / ctrlGridStep) - this.relativePosition.x!;
    const y = ctrlGridStep * Math.round(this.relativePosition.y! / ctrlGridStep) - this.relativePosition.y!;
    const moved = x !== 0 || y !== 0;
    this.moveBy({x, y});
    return moved;
  }

  @computed
  get labelNotUnique() {
    return (this.rackStore?.usedLabels[this.label] > 1 || this.rackStore?.usedLabels[this._label] > 1);
  }

  @computed
  get validationErrors() {
    return compact([
      !this.logicalDevice && 'Logical Device is not set',
      this.isMlagLeafPair && !this.mlagVlanId && 'MLAG Keepalive VLAN ID is not set',
      !this.label && 'Label is required',
      this.labelNotUnique && 'Label must be unique',
      this.rackStore?.isL3Clos && this.spineLinksCount < 1 && this.isLeaf && 'Links per spine are not defined',
      ...this.linkingErrors,
      ...compact(values(this.portChannelIdsErrors)),
      ...this.railsErrors
    ]);
  }

  @computed
  get linkingErrors() {
    const errors: string[] = [];

    // Generics and access switches must have link[s] to other node[s]
    if (!this.myLinksByType.null && !this.isLeaf) {
      errors.push('System must be connected with switch');
    }
    // If system is a switch pair ...
    if (this.isPaired) {
      if (this.isMlagLeafPair) {
        // ... of MLAG type ...
        // there must be at least 1 L2 peer link defined
        if (!this.myLinksByType[PEER_LINKS_TYPES.L2_PEER]) {
          errors.push('MLAG leaf pair must have at least one L2 peer link');
        }
      } else if (this.isAccessSwitch) {
        if (!this.myLinksByType[PEER_LINKS_TYPES.L3_PEER]) {
          // ... of ESI type ...
          // there must be at least 1 L3 peer link defined
          errors.push('ESI access switch pair must have at least one L3 peer link');
        }
        // ESI access switch pair can only be connected with ESI leaf pair
        if (some(
          this.myLinksGroups,
          ({toNode}) => (!toNode?.isPaired || toNode?.isMlagLeafPair)
        )) {
          errors.push('ESI access switch pair can only be connected to ESI leaf pair');
        }
      }
    }
    return errors;
  }

  fallsWithin(start: PositionType, end: PositionType): boolean {
    const {x, y} = this.position;
    const [startX, endX] = sortBy([start.x, end.x]);
    const [startY, endY] = sortBy([start.y, end.y]);

    return (x! < endX!) && (x! + ctrlNodeWidth > startX!) &&
      (y! < endY!) && (y! + ctrlNodeHeight > startY!);
  }

  clone(): Node {
    return new Node(toJS({
      id: generateLocalId(),
      label: this.rackStore?.pickUniqueLabel(this._label, this.isPaired) ?? `${this.label}_${generateLocalId()}`,
      name: generateLocalId(),
      role: this.role,
      logicalDevice: this.logicalDevice,
      zone: this.zone,
      tags: this.tags,
      portChannelId: this.portChannelId,
      portChannelIdExt: this.portChannelIdExt,
      relativePosition: {...this.relativePosition},
      mlagVlanId: this.mlagVlanId,
      spineLinksCount: this.spineLinksCount,
      spineLinksSpeed: this.spineLinksSpeed
    }), this.rackStore);
  }

  @computed
  get interfacesPositions(): Dictionary<InterfacePositionType> {
    // Defines which side of the node bar their interfaces must be shown
    // depending on the position of the targets they are connected to
    return transform(
      // For the simple link define position based on the single target node position
      this.myLinksGroups,
      (acc, linksGroup) => {
        const {id, railIndex, fromName, fromNode, toNode, isPeer, isEmpty, isAttachedToFirst,
          isAttachedToSecond} = linksGroup;
        if (isEmpty && isPeer) return;

        const isSource = fromName === this.name;
        const node = isSource ? toNode : fromNode;
        // If link is one to pair in some cases target switches are not affected
        if (
          this.isPaired && !isSource && !node?.isPaired &&
            !(this.isFirstInPair ? isAttachedToFirst : isAttachedToSecond)
        ) return;

        // Peer links are always left to right
        const side = isPeer ? (
          this.isFirstInPair ?
            ctrlIfcPositions.RIGHT :
            ctrlIfcPositions.LEFT
        ) : (
          // Cross-zone links are always bottom to top
          isSource ?
            ctrlIfcPositions.TOP :
            ctrlIfcPositions.BOTTOM
          );

        acc[id] = {id, railIndex, side, node};
      },
      {}
    );
  }

  // Checks if the new LD is still capable of hosting previously defined links to spines.
  // If not, reset these settings.
  @action
  checkLinksToSpines() {
    if (!this.isLeaf) return;
    const spinePorts = Port.generate(this.spineLinksCount, speedFromString(this.spineLinksSpeed), PORT_ROLES.SPINE);
    if (!tryPlacing(spinePorts, this.ldPorts)) {
      this.setProperty('spineLinksCount', 0, true);
      this.setProperty('spineLinksSpeed', null, true);
    }
  }

  isPairedWith(nodeId: string): boolean {
    return this.pairedWith?.id === nodeId;
  }

  static deserialize(jsonValue, rackStore: RackStore): Node {
    const {devices = {}, nodes = {}, zones = {}} = rackStore;
    const {
      id,
      label,
      name,
      'node-type': nodeType,
      tags,
      redundancy_protocol: redundancyProtocol,
      logical_device: logicalDeviceId,
      pair_id: pairId,
      mlag_vlan_id: mlagVlanId,
      link_per_spine_count: spineLinksCount,
      link_per_spine_speed: spineLinksSpeed,
      port_channel_id_min: portChannelId,
      port_channel_id_max: portChannelIdExt,
      internals
    } = jsonValue;

    const role = NODE_TYPES_ROLES[nodeType];
    const pairedWith = pairId ? nodes[pairId] : null;

    const node = new Node({
      id,
      label,
      name,
      role,
      tags,
      zone: zones[role],
      redundancyProtocol,
      logicalDevice: devices[logicalDeviceId],
      pairedWith,
      mlagVlanId,
      spineLinksCount,
      spineLinksSpeed: stringFromSpeed(spineLinksSpeed),
      portChannelId,
      portChannelIdExt,
      ...(internals ? internals : {})
    }, rackStore);

    // Pairing consistency must be preserved on both ends
    if (pairedWith) {
      pairedWith.setProperty('pairedWith', node);
    }
    return node;
  }

  serialize(withInternals = true) {
    return {
      id: this.id,
      label: this._label,
      'node-type': ROLES_NODE_TYPES[this.role],
      tags: toJS(this.tags),
      redundancy_protocol: this.redundancyProtocol,
      logical_device: this.logicalDevice?.id ?? null,
      ...(this.isPaired ? {pair_id: this.pairedWith?.id} : {}),
      ...(this.isLeaf ?
        {
          ...(this.isMlagLeafPair ? {mlag_vlan_id: this.mlagVlanId} : {}),
          ...(this.rackStore?.isL3Clos ?
            {
              link_per_spine_count: this.spineLinksCount,
              link_per_spine_speed: speedFromString(this.spineLinksSpeed)
            } : {}
          ),
          ...(this.isPaired ?
            {
              leaf_leaf_link_port_channel_id: 0,
              leaf_leaf_l3_link_port_channel_id: this.isLeaf ? 0 : this.portChannelIdExt
            } : {}
          ),
          ...(this.isMlagLeafPair ?
            {
              leaf_leaf_link_port_channel_id: this.portChannelId,
              leaf_leaf_l3_link_port_channel_id: this.portChannelIdExt
            } : {}
          )
        } :
        {}
      ),
      ...(this.isAccessSwitch ?
        {
          access_access_link_port_channel_id_min: this.portChannelIdExt,
          access_access_link_port_channel_id_max: this.portChannelIdExt

        } :
        {}
      ),
      ...(this.isGeneric ?
        {
          port_channel_id_min: this.portChannelId,
          port_channel_id_max: this.portChannelIdExt
        } :
        {}
      ),
      ...(withInternals ? {
        internals: {
          name: this.name,
          relativePosition: toJS(this.relativePosition),
          isFirstInPair: this.isFirstInPair,
          portChannelId: this.portChannelId,
          portChannelIdExt: this.portChannelIdExt
        }
      } : {})
    };
  }
}

export default Node;
